import {
  DEFAULT_COMPANY_LANGUAGE,
  isLanguageISO6391,
  LanguageISO6391,
} from 'ai-constants'

type LanguageScope = {
  lang: LanguageISO6391
}

type LanguageScopeStorage = {
  getStore: () => LanguageScope | undefined
  enterWith: (scope: LanguageScope) => void
}

type LanguageScopeStorageConstructor = new () => LanguageScopeStorage

export type LanguageOwner = {
  id: string
  depth: number
  lang: LanguageISO6391
}

type ActiveLanguageState = {
  serverScope?: LanguageScopeStorage
  clientOwner?: LanguageOwner
}

declare global {
  var i11nActiveLanguageState: ActiveLanguageState | undefined
}

const isServer = () => typeof window === 'undefined'

const isLanguageScopeStorageConstructor = (
  value: unknown,
): value is LanguageScopeStorageConstructor => typeof value === 'function'

const createServerScope = () => {
  if (!isServer()) return

  const asyncLocalStorage = Object.getOwnPropertyDescriptor(
    globalThis,
    'AsyncLocalStorage',
  )?.value

  if (!isLanguageScopeStorageConstructor(asyncLocalStorage)) return

  return new asyncLocalStorage()
}

const getActiveLanguageState = () => {
  globalThis.i11nActiveLanguageState ??= { serverScope: createServerScope() }

  return globalThis.i11nActiveLanguageState
}

const getDocumentLanguage = () => {
  if (typeof document === 'undefined') return

  const { lang } = document.documentElement

  if (!isLanguageISO6391(lang)) return

  return lang
}

export const activateLanguage = (owner: LanguageOwner) => {
  const state = getActiveLanguageState()

  if (state.serverScope) {
    state.serverScope.enterWith({ lang: owner.lang })
    return
  }

  if (isServer()) return

  const currentOwner = state.clientOwner
  const isShadowedByDeeperOwner =
    currentOwner &&
    currentOwner.id !== owner.id &&
    currentOwner.depth > owner.depth

  if (isShadowedByDeeperOwner) return

  state.clientOwner = owner
}

export const releaseLanguage = (ownerId: string, parent?: LanguageOwner) => {
  const state = getActiveLanguageState()

  if (state.clientOwner?.id !== ownerId) return

  state.clientOwner = parent
}

export const isActiveLanguageOwner = (ownerId: string) =>
  getActiveLanguageState().clientOwner?.id === ownerId

export const getCurrentLanguage = (): LanguageISO6391 => {
  const state = getActiveLanguageState()

  return (
    state.serverScope?.getStore()?.lang ??
    state.clientOwner?.lang ??
    getDocumentLanguage() ??
    DEFAULT_COMPANY_LANGUAGE
  )
}
